import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { formatReceiptPlainText, buildRawbtUri } from '../utils/rawbtFormat';

/**
 * Standalone receipt-print page (`/print/:orderNumber`).
 *
 * Why a dedicated route?
 *   The previous iframe-injection approach (printReceipt.js) prints through
 *   the Android Chrome → PDF → RAWBT pipeline, which ignores `@page size:
 *   80mm` on mobile and produces a microscopic, faint output (~38% scale of
 *   A4 squeezed to 80mm). This route bypasses that pipeline entirely by:
 *
 *     1. Rendering the receipt as plain text (printer's built-in font).
 *     2. Triggering the `rawbt:base64,<utf8>` URI scheme, which delivers
 *        the text directly to the RAWBT driver — no rasterization, no
 *        scaling, no Chrome print-engine bugs.
 *
 *   Desktop / iOS fallback: a second button calls `window.print()` on a
 *   styled HTML rendering of the same content (handled by the parent CSS
 *   below).
 *
 * Debugging: open `/print/BB-12345` directly in any browser to see exactly
 * what would be sent to the printer.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrintReceipt = () => {
  const { orderNumber } = useParams();
  const [searchParams] = useSearchParams();
  const auto = searchParams.get('auto'); // 'rawbt' | 'browser' | null

  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const rawbtLinkRef = useRef(null);
  const autoFiredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/orders/${orderNumber}`);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setOrder(data);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Hiba történt a rendelés betöltésekor.');
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [orderNumber]);

  // Auto-trigger after order loads. 500 ms delay lets the DOM settle so the
  // <a> ref is populated; programmatic .click() on a fresh anchor is the
  // most reliable way to fire a custom URI scheme on Android Chrome (more
  // reliable than `window.location.href = 'rawbt:...'`, which the browser
  // sometimes treats as a navigation error if no handler is registered).
  useEffect(() => {
    if (!order || autoFiredRef.current) return;
    if (auto === 'rawbt') {
      autoFiredRef.current = true;
      const t = setTimeout(() => {
        if (rawbtLinkRef.current) {
          rawbtLinkRef.current.click();
        }
      }, 500);
      return () => clearTimeout(t);
    }
    if (auto === 'browser') {
      autoFiredRef.current = true;
      const t = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(t);
    }
  }, [order, auto]);

  if (loading) {
    return (
      <div style={styles.loadingWrap}>
        <p style={styles.loadingText}>Rendelés betöltése…</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div style={styles.loadingWrap}>
        <p style={styles.errorText}>
          Hiba: {error || 'Rendelés nem található.'}
        </p>
        <p style={styles.errorHint}>Rendelésszám: {orderNumber}</p>
      </div>
    );
  }

  const plainText = formatReceiptPlainText(order);
  const rawbtUri = buildRawbtUri(order);

  return (
    <div style={styles.page}>
      {/* Action bar — visible on screen only, hidden in print */}
      <div className="print-actions" style={styles.actions}>
        <a
          ref={rawbtLinkRef}
          href={rawbtUri}
          style={styles.btnRawbt}
        >
          🖨️ Nyomtatás (RAWBT)
        </a>
        <button
          type="button"
          onClick={() => window.print()}
          style={styles.btnBrowser}
        >
          🖨️ Böngésző nyomtatás
        </button>
        <button
          type="button"
          onClick={() => window.close()}
          style={styles.btnClose}
        >
          Bezárás
        </button>
      </div>

      {/* The receipt itself — rendered as <pre> so the monospace alignment
          carries over to window.print() as well. */}
      <pre className="print-receipt" style={styles.receipt}>
        {plainText}
      </pre>

      {/* Page-scoped print styles. */}
      <style>{`
        @page { size: 80mm auto; margin: 0; }
        html, body { background: #fff; margin: 0; padding: 0; }

        @media print {
          .print-actions { display: none !important; }
          .print-receipt {
            width: 80mm;
            margin: 0;
            padding: 3mm;
            font-family: 'Courier New', monospace;
            font-size: 11px;
            line-height: 1.25;
            color: #000;
            white-space: pre;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f3f4f6',
    padding: '16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '16px',
    padding: '16px',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    position: 'sticky',
    top: '8px',
    zIndex: 10,
  },
  btnRawbt: {
    flex: '1 1 200px',
    minHeight: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(90deg, #00BCD4, #00ACC1)',
    color: '#fff',
    fontWeight: 700,
    fontSize: '16px',
    textDecoration: 'none',
    borderRadius: '8px',
    padding: '12px 20px',
  },
  btnBrowser: {
    flex: '1 1 200px',
    minHeight: '56px',
    background: '#FFA726',
    color: '#fff',
    fontWeight: 700,
    fontSize: '16px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    padding: '12px 20px',
  },
  btnClose: {
    flex: '0 0 auto',
    minHeight: '56px',
    background: '#e5e7eb',
    color: '#374151',
    fontWeight: 600,
    fontSize: '14px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    padding: '12px 16px',
  },
  receipt: {
    maxWidth: '480px',
    margin: '0 auto',
    padding: '16px',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontFamily: '"Courier New", monospace',
    fontSize: '13px',
    lineHeight: 1.4,
    color: '#000',
    whiteSpace: 'pre',
    overflowX: 'auto',
  },
  loadingWrap: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f3f4f6',
    padding: '24px',
    textAlign: 'center',
  },
  loadingText: {
    fontSize: '18px',
    color: '#374151',
  },
  errorText: {
    fontSize: '18px',
    color: '#dc2626',
    fontWeight: 700,
    marginBottom: '8px',
  },
  errorHint: {
    fontSize: '14px',
    color: '#6b7280',
  },
};

export default PrintReceipt;
